import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { StoreProps } from 'store';
import { addPermissions, WithPermissions } from 'decorators/addPermissions';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import PaymentPageUI from 'types/PaymentPageUI';
import PaymentItemType from 'types/savedEntity/SavedPayment';
import Mapper from 'helpers/Mapper';
import Operations from 'helpers/Operations';
import Messages from 'constants/rpcTypes';
import permissionReasons from 'constants/permissionReasons';

import Animation from 'components/ui/animation';
import PageTemplate from 'components/pageTemplate/PageTemplate';
import NoContent from 'components/ui/noContent';
import Teaser from 'components/ui/teaser';
import PaymentActions from 'components/paymentCard/paymentActions/PaymentActionsContainer';
import { Config } from 'components/paymentCard/PaymentCardContainer';
import OperationItem from 'components/paymentCard/operationItem';
import CustomScrollbar from 'components/ui/customScrollbar';
import CopyToClipboard from 'components/ui/copyToClipboard';
import OperationInfo from 'components/paymentCard/operationInfo';
import AddBlackList from 'components/paymentCard/panelBlackList/AddBlackListContainer';
import ButtonReference from 'components/paymentCard/buttonReference';
import CreateSupportTicketButton from 'components/createSupportTicketButton/CreateSupportTicketButton';
import PaymentInfo from 'components/paymentCard/paymentInfo';
import Button from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { Tab, TabsContainer as Tabs } from 'components/ui/tabs';
import { CallbackInfo } from './callbackInfo';
import RoundButton from 'components/ui/roundButton';
import LabelStatus from 'components/ui/labelStatus';
import Panel from 'components/ui/panel';
import { ReactComponent as NoContentImage } from 'images/no-content.svg';
import { usePaymentCard } from './usePaymentCard';
import './paymentCard.scss';

interface Props extends IntlProps, WithPermissions, StoreProps {
  payment?: PaymentItemType;
  config?: Config;
  projectId?: string;
  isLoading: boolean;
  syncPaymentUI: (id: string, ui: PaymentPageUI) => void;
  fetchData: () => void;
}

export function PaymentCard(props: Props) {
  const {
    payment,
    config,
    isLoading,
    getTranslate,
    isEnabled,
    isDisabledByReason,
    syncPaymentUI,
    fetchData,
  } = props;

  const {
    pageUI,
    isOpenedPaymentInfo,
    isActionsOpened,
    changePageUI,
    setIsActionsOpened,
    openPaymentInfo,
    closePaymentInfo,
    changeSort,
    openRetries,
    closeRetries,
    openRetryCancelModal,
    getSortedOperations,
    getCurrentOperations,
    getCurrentSelectedOperation,
    getStatusTooltip,
  } = usePaymentCard({
    payment,
    config,
    isLoading,
    getTranslate,
    isEnabled,
    isDisabledByReason,
    syncPaymentUI,
    fetchData,
  });

  const renderOperationItem = (operations: any[]) => {
    const { selectedOperationId, operationOrder, operationsScrollTop } = pageUI;

    if (!operations) {
      return null;
    }

    const currentOperations = getCurrentOperations(operations);
    const sortedOperations = getSortedOperations(currentOperations, operationOrder as 'asc' | 'desc');

    return (
      <CustomScrollbar
        initialScrollTop={operationsScrollTop}
        needResetScrollTop
        onScrollStop={({ operationsScrollTop }) =>
          changePageUI({ operationsScrollTop })
        }>
        <div className='payment-card__operations-list border-border-base h-full min-h-0 border-t pb-5'>
          {sortedOperations.length
            ? sortedOperations.map((operation) => {
              const { operationId: currentOperationId } = operation;
              return (
                <OperationItem
                  key={currentOperationId}
                  data={operation}
                  isCurrent={currentOperationId === selectedOperationId}
                  onSelect={(selectedOperationId) =>
                    changePageUI({ selectedOperationId })
                  }
                  onStatusClick={(operationId) => openRetries(operationId)}
                />
              );
            })
            : null}
        </div>
      </CustomScrollbar>
    );
  };

  const renderOperationsList = () => {
    const { operations } = payment?.data || {};
    const { isRetriesOpen, parentSelectedOperationId, operationOrder } = pageUI;
    const operationsCount = !isLoading && operations ? operations.length : '';
    const opsDuration =
      !isLoading && operations ? Operations.getDuration(operations || []) : '';
    const paymentTitle = getTranslate('payments.data.paymentOperations.header');
    const operationsCountText = getTranslate('payments.single.operation', {
      operationsCount,
    });
    const operationDurationText = getTranslate('payments.single.duration', {
      operationsDuration: opsDuration,
    });
    const paymentOperationCount = `${operationsCountText} | ${operationDurationText}`;
    const retryTitle = getTranslate(
      'subscriptions.single.recurringRetries.header'
    );

    return (
      <div className='payment-card__operations-card card flex h-full flex-col'>
        <div className='payment-card__operations-header relative flex items-center justify-between gap-2 px-5 py-4'>
          <div className='payment-card__operations-header-info overflow-hidden'>
            <div className='payment-card__operations-title text-l-bold mb-1'>
              {isRetriesOpen ? retryTitle : paymentTitle}
            </div>

            {opsDuration ? (
              <div className='payment-card__operations-meta text-m-regular text-text-secondary flex gap-1'>
                <span>{getTranslate('subscriptions.single.Id.label')}</span>
                <span className='payment-card__operations-meta-id utils-overflow-dots'>
                  {isRetriesOpen
                    ? parentSelectedOperationId
                    : paymentOperationCount}
                </span>

                {isRetriesOpen ? (
                  <CopyToClipboard text={parentSelectedOperationId} />
                ) : null}
              </div>
            ) : null}
          </div>

          <div className='payment-card__operations-controls flex items-center gap-2'>
            {isRetriesOpen ? (
              <RoundButton
                size={26}
                icon='im-Arrow-left-Option-2'
                iconSize={12}
                onClick={closeRetries}
                customClass='payment-card__operations-back'
              />
            ) : null}

            {operations?.length ? (
              <RoundButton
                size={26}
                icon='im-Sort'
                iconSize={12}
                onClick={changeSort}
                customClass={classNames('payment-card__operations-sort', {
                  'rotate-180': operationOrder === 'asc',
                })}
              />
            ) : null}
          </div>
        </div>

        <div className='payment-card__operations-content relative h-full min-h-0'>
          {isLoading || !operations ? (
            <div className='payment-card__operations-skeleton px-5'>
              <Skeleton width='100%' height={80} />
            </div>
          ) : (
            renderOperationItem(operations)
          )}
        </div>
      </div>
    );
  };

  const renderOperationInfoActions = (operations: any[]) => {
    const { isAddToList } = config || {};
    const { isRetriesOpen } = pageUI;
    const { transactionId, projectId } = payment?.data?.payment || {};

    const {
      operationId,
      operationType,
      availableActions,
      mapped_recurring_retry_status: retryStatus,
    } = getCurrentSelectedOperation(operations);

    const referenceButtonEnabled =
      availableActions?.operationReferenceOrder?.isEnabled;
    const isEnabledToCreateTicket =
      isEnabled(Messages.PaymentIssue_PerformAction) ||
      isDisabledByReason(
        Messages.PaymentIssue_PerformAction,
        permissionReasons.REASON_IS_NOT_AVAILABLE_FOR_SUPPORT
      );
    const isCancelEnabled =
      isEnabled(Messages.SubscriptionPayment_RetryCancel) ||
      isDisabledByReason(
        Messages.SubscriptionPayment_RetryCancel,
        permissionReasons.REASON_IS_NOT_AVAILABLE_FOR_SUPPORT
      );

    const actions = (
      <>
        {referenceButtonEnabled ? (
          <ButtonReference
            transactionId={transactionId}
            operationId={operationId}
          />
        ) : null}

        {isEnabledToCreateTicket ? (
          <CreateSupportTicketButton
            operationId={operationId}
            transactionId={transactionId}
          />
        ) : null}

        {isAddToList ? (
          <AddBlackList
            transactionId={transactionId}
            operationId={operationId}
          />
        ) : null}

        {operationType === 'recurring' ? (
          <PaymentActions
            key={transactionId}
            isNeedPing={false}
            operationId={operationId}
            transactionId={transactionId}
            projectId={projectId}
            refundTooltip={getTranslate(
              'payments.data.operationRefund.info.tooltip'
            )}
            customClass='payment-actions_card'
            buttonTheme='primary'
          />
        ) : null}
      </>
    );

    return (
      <div className='payment-card__actions mt-4 flex flex-wrap gap-2'>
        {retryStatus ? (
          <>
            {isRetriesOpen ? (
              <Button
                status='outline'
                text={getTranslate(
                  'payments.single.backToMainOperation.button'
                )}
                onClick={closeRetries}
              />
            ) : (
              <>
                <Button
                  status='primary'
                  text={getTranslate('payments.single.showretries.button')}
                  onClick={() => openRetries(operationId)}
                />
                <Button
                  status='danger-outline'
                  text={getTranslate('payments.single.cancelretries.button')}
                  onClick={openRetryCancelModal}
                  disabled={
                    !isCancelEnabled || retryStatus !== 'retries active'
                  }
                />
              </>
            )}

            <div className='relative'>
              <Button
                icon='im-Dots'
                status='primary'
                customClass='payment-card__actions-toggle h-[32px] w-[32px] rounded-full'
                onClick={() => setIsActionsOpened(true)}
              />

              <Panel
                isOpened={isActionsOpened}
                renderCloseControl={false}
                contentStyle={{
                  padding: 12,
                  overflow: 'unset',
                }}
                onClose={() => setIsActionsOpened(false)}
                customClass='max-tablet:left-[unset] max-tablet:right-[-40px] top-[40px] right-[unset] left-0'>
                <div className='payment-card__actions-panel flex flex-col gap-1'>
                  {actions}
                </div>
              </Panel>
            </div>
          </>
        ) : (
          actions
        )}
      </div>
    );
  };

  const renderOperationInfoHeader = (operations: any[]) => {
    const selectedOperation = getCurrentSelectedOperation(operations);

    if (!isLoading && !selectedOperation) {
      return null;
    }

    const {
      operationId,
      next_retry_date: retryDate,
      mapped_recurring_retry_status: retryStatus,
    } = selectedOperation;

    return (
      <div className='payment-card__operation-info-header p-5'>
        <div className='payment-card__operation-info-header-row flex flex-wrap items-center justify-between gap-2'>
          <div className='payment-card__operation-info-id-block'>
            <div className='payment-card__operation-info-id-label text-m-regular text-text-secondary mb-1'>
              {getTranslate('payments.single.operationCard.header')} ID
            </div>
            <div className='payment-card__operation-info-id-value font-heading text-heading-m flex items-center gap-2'>
              <span className='payment-card__operation-info-id-text'>
                {operationId}
              </span>
              <CopyToClipboard
                customClass='payment-card__operation-info-id-copy'
                text={operationId}
              />
            </div>
          </div>
          {retryStatus ? (
            <div className='payment-card__operation-info-status'>
              <LabelStatus
                text={retryStatus}
                status={Mapper.getStatus(retryStatus)}
                customClass='rounded-[44px] px-[10px] py-[3px] leading-[20px] capitalize'
              />
            </div>
          ) : null}
        </div>
        {retryStatus ? (
          <div className='payment-card__operation-info-tooltip pt-3'>
            {getStatusTooltip(retryStatus, retryDate)}
          </div>
        ) : null}
        <div className='payment-card__operation-info-actions'>
          {renderOperationInfoActions(operations)}
        </div>
      </div>
    );
  };

  const renderOperationInfoTabs = (operations: any[]) => {
    const { transactionId } = payment?.data?.payment || {};
    const selectedOperation = getCurrentSelectedOperation(operations);

    if (!isLoading && !selectedOperation) {
      return null;
    }

    const { operationId, callback, availableActions } = selectedOperation;
    const hasCallback = callback?.callbackBody;
    const resendCallbackEnabled = availableActions?.callbackResend?.isEnabled;

    return (
      <Tabs theme='brand' customClass='payment-card__tabs flex h-full flex-col'>
        <Tab title={getTranslate('payments.single.operationInfo.header')}>
          <CustomScrollbar
            customHeight='100%'
            initialScrollTop={pageUI.operationInfoScrollTop}
            needResetScrollTop
            onScrollStop={({ operationInfoScrollTop }) =>
              changePageUI({ operationInfoScrollTop })
            }>
            <div className='p-5'>
              <OperationInfo
                transactionId={transactionId}
                operation={selectedOperation}
                pageUI={pageUI}
                onChangePageUI={changePageUI}
              />
            </div>
          </CustomScrollbar>
        </Tab>
        {hasCallback ? (
          <Tab title={getTranslate('payments.single.lastcallback.header')}>
            <CallbackInfo
              transactionId={transactionId}
              operationId={operationId}
              callback={callback}
              resendEnabled={resendCallbackEnabled}
            />
          </Tab>
        ) : null}
      </Tabs>
    );
  };

  const renderOperationInfo = () => {
    const { operations } = payment?.data || {};
    const { parentSelectedOperationId } = pageUI;

    const noContentText = getTranslate(
      'subscriptions.single.recurringRetries.noInfo'
    );

    let currentOperation: any[] | null = null;

    if (operations && !isLoading) {
      currentOperation = getCurrentOperations(operations);
    }

    if (currentOperation && !currentOperation.length) {
      const selectedOperation = operations.find(
        ({ operationId }) => operationId === parentSelectedOperationId
      );
      const { next_retry_date: retryDate } = selectedOperation;

      return (
        <div className='payment-card__no-content card flex h-full items-center justify-center'>
          <div className='payment-card__no-content-inner px-5'>
            <div className='payment-card__no-content-body flex flex-col items-center justify-center gap-3 text-center'>
              <NoContentImage />
              <h1 className='payment-card__no-content-title font-heading text-heading-m'>
                {noContentText}
              </h1>

              {retryDate ? (
                <p className='payment-card__no-content-text text-m-regular max-w-[305px]'>
                  {getTranslate(
                    'subscriptions.single.recurringRetries.nextTry'
                  )}
                  &nbsp;
                  <span className='text-m-semi-bold'>{retryDate}</span>
                </p>
              ) : null}

              <Button
                status='outline'
                text={getTranslate(
                  'payments.single.backToMainOperation.button'
                )}
                onClick={closeRetries}
                customClass='payment-card__no-content-button'
              />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className='payment-card__operation-info-body flex h-full flex-col gap-4'>
        <div className='payment-card__operation-info-card card'>
          {isLoading || !operations ? (
            <div className='payment-card__operation-info-skeleton p-5'>
              <Skeleton width='100%' height={80} />
            </div>
          ) : (
            renderOperationInfoHeader(currentOperation)
          )}
        </div>

        <div className='payment-card__operation-info-tabs card relative h-full min-h-0'>
          {isLoading || !operations ? (
            <div className='payment-card__operation-info-skeleton p-5'>
              <Skeleton width='100%' height={80} />
            </div>
          ) : (
            renderOperationInfoTabs(currentOperation)
          )}
        </div>
      </div>
    );
  };

  const renderPaymentInfo = () => {
    const { isManagePayment, isCustomerInfo, isRemittanceRecipientAccount } =
      config || {};

    return (
      <PaymentInfo
        theme='brand'
        isLoading={isLoading}
        closePaymentInfo={closePaymentInfo}
        data={payment?.data}
        pageUI={pageUI}
        onChangePageUI={changePageUI}
        isManagePayment={isManagePayment}
        isCustomerInfo={isCustomerInfo}
        isRemittanceRecipientAccount={isRemittanceRecipientAccount}
      />
    );
  };

  const renderTeaser = () => {
    const { paymentId, transactionId, projectId, paymentStatus } =
      payment?.data?.payment || {};
    const { isManagePayment } = config || {};

    return (
      <Teaser
        title={getTranslate('payments.data.payment.header')}
        text={
          paymentId ? (
            <div className='payment-card__teaser-id flex items-end gap-2'>
              <span className='payment-card__teaser-id-text'>{paymentId}</span>
              <CopyToClipboard
                text={paymentId}
                customClass='payment-card__teaser-id-copy text-text-always-white hover:opacity-80'
              />
            </div>
          ) : (
            ''
          )
        }
        status={paymentStatus}
        isLoading={isLoading}>
        <Button
          text={getTranslate('payments.single.viewPaymentInfo.button')}
          onClick={openPaymentInfo}
          status='outline-border'
          size='normal'
          customClass='payment-card__view-info-button max-tablet-l:inline-block hidden'
        />
        {isManagePayment ? (
          <PaymentActions
            key={transactionId}
            transactionId={transactionId}
            projectId={projectId}
            buttonTheme='light'
            customClass='payment-card__teaser-actions payment-actions_card'
          />
        ) : null}
      </Teaser>
    );
  };

  const renderContent = () => (
    <div className='payment-card max-tablet:h-auto size-full'>
      <div className='size-full'>
        <div className='payment-card__wrapper'>
          <div className='payment-card__payment-details'>
            {renderTeaser()}
          </div>
          <div className='payment-card__operations'>
            {renderOperationsList()}
          </div>
          <div className='payment-card__operation-info'>
            {renderOperationInfo()}
          </div>

          {isOpenedPaymentInfo ? (
            <div className='payment-card__payment-info'>
              {renderPaymentInfo()}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <PageTemplate.Container customClass='overflow-y-auto'>
      <div className='max-tablet:h-auto size-full'>
        <Animation>
          <div className='max-mobile-s:min-h-auto relative mx-auto size-full min-h-[610px] max-w-[1640px]'>
            {payment?.data?.error ? (
              <NoContent
                text={getTranslate(
                  'payments.single.paymentInfo.noPaymentDetails.text'
                )}
              />
            ) : (
              renderContent()
            )}
          </div>
        </Animation>
      </div>
    </PageTemplate.Container>
  );
}

export default connect()(addTranslation(addPermissions(PaymentCard)));
